import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { MODAL_WIDTHS } from './constants';
import { resolveVideoUrl, resolveIframeSrc } from './helpers';

// ── Base animated modal shell ─────────────────────────────────────────────────
export function EngineModal({ open, onClose, width = 720, children }) {
  const [mounted, setMounted]   = useState(false);
  const [visible, setVisible]   = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!mounted) return null;
  const isFullscreen = width === '100%';

  return (
    <Box
      onClick={onClose}
      sx={{ position: 'absolute', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: visible ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0)', transition: 'background 0.25s ease', backdropFilter: visible ? 'blur(4px)' : 'none' }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={{ position: 'relative', width: isFullscreen ? '100%' : `min(${width}px, calc(100% - 32px))`, maxHeight: isFullscreen ? '100%' : 'calc(100% - 32px)', background: '#111', borderRadius: isFullscreen ? 0 : '14px', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', transform: visible ? 'scale(1)' : 'scale(0.88)', opacity: visible ? 1 : 0, transition: 'transform 0.28s cubic-bezier(0.34,1.2,0.64,1), opacity 0.22s ease' }}
      >
        <Box onClick={onClose} sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 700, transition: 'all 0.15s', '&:hover': { background: 'rgba(220,38,38,0.8)', borderColor: 'transparent', color: '#fff' } }}>✕</Box>
        {children}
      </Box>
    </Box>
  );
}

// ── Video modal ───────────────────────────────────────────────────────────────
export function VideoModal({ hotspot, onClose }) {
  const action   = hotspot.hotspotAction;
  const provider = action?.videoProvider || 'url';
  const src      = resolveVideoUrl(provider, action?.videoUrl || '');
  const isEmbed  = provider === 'youtube' || provider === 'vimeo';

  return (
    <EngineModal open onClose={onClose} width={MODAL_WIDTHS.lg}>
      <Box sx={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
        {isEmbed ? (
          <iframe src={src} title={hotspot.label || 'Video'} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        ) : (
          <video src={src} autoPlay={action?.autoplay} controls={action?.controls !== false} playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
        )}
      </Box>
    </EngineModal>
  );
}

// ── Iframe modal ──────────────────────────────────────────────────────────────
export function IframeModal({ hotspot, onClose }) {
  const action     = hotspot.hotspotAction;
  const widthKey   = action?.modalWidth || 'md';
  const modalWidth = MODAL_WIDTHS[widthKey] || MODAL_WIDTHS.md;
  const src        = resolveIframeSrc(action?.embedCode || '');
  const isFullscreen = widthKey === 'full';

  return (
    <EngineModal open onClose={onClose} width={modalWidth}>
      {src ? (
        <Box sx={{ width: '100%', height: isFullscreen ? '100vh' : '70vh', minHeight: 300 }}>
          <iframe src={src} title={hotspot.label || 'Content'} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen" allowFullScreen style={{ width: '100%', height: '100%', display: 'block', border: 'none' }} />
        </Box>
      ) : (
        <Box sx={{ width: '100%', minHeight: 200, p: 2, color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }} dangerouslySetInnerHTML={{ __html: action?.embedCode || '' }} />
      )}
    </EngineModal>
  );
}

// ── Gallery modal ─────────────────────────────────────────────────────────────
export function GalleryModal({ hotspot, onClose }) {
  const images  = hotspot.hotspotAction?.images || [];
  const [idx,     setIdx]     = useState(0);
  const [zoomed,  setZoomed]  = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setMounted(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
  }, []);

  const close = () => { setVisible(false); setTimeout(onClose, 280); };
  useEffect(() => { setZoomed(false); }, [idx]);

  const handleMouseMove = useCallback((e) => {
    if (!zoomed || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    setZoomPos({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
  }, [zoomed]);

  const prev = () => setIdx(i => Math.max(0, i - 1));
  const next = () => setIdx(i => Math.min(images.length - 1, i + 1));

  if (!mounted) return null;
  const curr = images[idx];

  return (
    <Box onClick={close} sx={{ position: 'absolute', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: visible ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0)', transition: 'background 0.25s ease', backdropFilter: visible ? 'blur(6px)' : 'none' }}>
      <Box onClick={e => e.stopPropagation()} sx={{ position: 'relative', width: 'min(960px, calc(100% - 32px))', maxHeight: 'calc(100% - 32px)', background: '#0a0a0a', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', transform: visible ? 'scale(1)' : 'scale(0.88)', opacity: visible ? 1 : 0, transition: 'transform 0.28s cubic-bezier(0.34,1.2,0.64,1), opacity 0.22s ease', display: 'flex', flexDirection: 'column' }}>
        {/* Close */}
        <Box onClick={close} sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 700, '&:hover': { background: 'rgba(220,38,38,0.8)', color: '#fff' } }}>✕</Box>

        {/* Hint badge */}
        <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 10, px: 1.25, py: 0.4, borderRadius: '20px', background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', fontWeight: 600, pointerEvents: 'none' }}>
          {zoomed ? 'Move to pan · Click to zoom out' : 'Click image to zoom'}
        </Box>

        {/* Image area */}
        <Box ref={imgRef} onMouseMove={handleMouseMove} onClick={() => setZoomed(v => !v)} sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', overflow: 'hidden', cursor: zoomed ? 'zoom-out' : 'zoom-in', position: 'relative', minHeight: 300 }}>
          <Box component="img" src={curr?.url} alt={curr?.caption || `Slide ${idx + 1}`} sx={{ display: 'block', maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', userSelect: 'none', transition: zoomed ? 'none' : 'transform 0.3s ease', transform: zoomed ? 'scale(2.2)' : 'scale(1)', transformOrigin: `${zoomPos.x}% ${zoomPos.y}%` }} />
          {idx > 0                 && <Box onClick={e => { e.stopPropagation(); prev(); }} sx={{ position: 'absolute', left: 12,  top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 18, '&:hover': { background: 'rgba(0,0,0,0.8)' }, zIndex: 5 }}>‹</Box>}
          {idx < images.length - 1 && <Box onClick={e => { e.stopPropagation(); next(); }} sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 18, '&:hover': { background: 'rgba(0,0,0,0.8)' }, zIndex: 5 }}>›</Box>}
        </Box>

        {/* Caption + dots */}
        <Box sx={{ px: 3, py: 1.5, background: '#111', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {curr?.caption && <Box sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>{curr.caption}</Box>}
          {images.length > 1 && (
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {images.map((_, i) => <Box key={i} onClick={() => setIdx(i)} sx={{ width: i === idx ? 20 : 7, height: 7, borderRadius: 100, background: i === idx ? '#fff' : 'rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s' }} />)}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}